// Marks a collapsed run of pages in a paginator window.
export const GAP = "gap" as const;

export type PageSlot = number | typeof GAP;

// Pages shown either side of the current one, once the window is floating
// in the middle.
const NEIGHBOURS = 1;

// Fixed width of a window: first + gap + current's run + gap + last.
const MAX_SLOTS = 7;

// Pages shown in a run when the current page is near an edge, where there
// is no gap on that side to spend width on.
const EDGE_RUN = MAX_SLOTS - 2;

const range = (from: number, to: number): number[] =>
  Array.from({ length: to - from + 1 }, (_, i) => from + i);

// Build the list of page slots to render: always the first and last page,
// the current page with a neighbour either side, and gaps standing in for
// everything else (#10593).
//
// The width is constant, so the control doesn't jitter as the user pages.
// That's why the run grows when the current page is near an edge: there's
// no gap on that side, and spending the freed width on reachable pages
// beats leaving a hole. Otherwise page 1 of 25 would offer only 1, 2 and
// 25 — technically "enumerated", but pages 3-5 would cost two clicks each.
//
// A gap always stands in for at least two pages: collapsing a single page
// costs a click and saves no width.
export function pageWindow(current: number, total: number): PageSlot[] {
  if (total < 1) return [];
  if (total <= MAX_SLOTS) return range(1, total);

  const page = Math.min(Math.max(current, 1), total);
  const left = Math.max(page - NEIGHBOURS, 2);
  const right = Math.min(page + NEIGHBOURS, total - 1);

  const leftGap = left > 3;
  const rightGap = right < total - 2;

  // Past MAX_SLOTS at least one gap is always drawn: a window with
  // neither would mean the current page is near both ends at once, which
  // only happens for a total the early return above already handled.
  if (!leftGap) return [...range(1, EDGE_RUN), GAP, total];
  if (!rightGap) return [1, GAP, ...range(total - EDGE_RUN + 1, total)];
  return [1, GAP, ...range(left, right), GAP, total];
}
